import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { callAPI } from '../hooks/api';
import { GlobalContext } from '../context/GlobalContext';
import { Button } from '../shadcn/Button';
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	useReactTable,
	getSortedRowModel,
	SortingState,
	getFilteredRowModel,
	getPaginationRowModel
} from '@tanstack/react-table';
import {
	ChevronDown,
	ChevronUp,
	ChevronsUpDown,
	Search,
	Plus,
	Pencil,
	Trash2,
	PackageOpen,
	RefreshCw,
	ChevronLeft,
	ChevronRight,
	Layers,
	Smartphone,
	Check
} from 'lucide-react';
import classNames from 'classnames';
import Input from '../components/Input';
import Select from '../components/Select';
import { Switch } from '../shadcn/Switch';
import { Dialog } from '../components/Dialog';
import { ConfirmDialog } from '../components/ConfirmDialog';
import Notice from '../components/Notice';
import Spinner from '../components/Spinner';
import ProductTable, { SelectableItem, formatStorageOrRam } from '../components/ProductTable';

interface DeviceModel {
	id: string;
	brand: string;
	model: string;
	device_commercial_name: string;
	available_colors?: (string | { name: string; hex: string })[];
	display?: string;
	storage?: number;
	ram?: number;
	processor?: string;
	battery?: string;
	created_at: string;
	updated_at: string;
}

interface StoreProduct {
	id: string;
	price: number;
	quantity: number | null;
	available: boolean;
	product_info: {
		brand: string;
		model: string;
		device_commercial_name: string;
		display?: string;
		storage?: number;
		ram?: number;
		processor?: string;
		battery?: string;
		available_colors?: (string | { name: string; hex: string })[];
		created_at: string;
		updated_at: string;
	};
}

const productColumnHelper = createColumnHelper<StoreProduct>();

function SortHeader({ column, children }: { column: any; children: React.ReactNode }) {
	const sorted = column.getIsSorted();
	return (
		<button
			className="flex items-center gap-1 text-neutral-400 hover:text-neutral-200 transition-colors group"
			onClick={() => column.toggleSorting(sorted === 'asc')}
		>
			<span className="text-[11px] font-semibold uppercase tracking-wider">{children}</span>
			<span className="text-neutral-500 group-hover:text-neutral-400">
				{sorted === 'asc' ? (
					<ChevronUp className="size-3.5" />
				) : sorted === 'desc' ? (
					<ChevronDown className="size-3.5" />
				) : (
					<ChevronsUpDown className="size-3.5 opacity-50" />
				)}
			</span>
		</button>
	);
}

function TableSkeleton() {
	return (
		<div className="animate-pulse flex flex-col min-h-0 flex-1">
			{/* Filter bar skeleton */}
			<div className="shrink-0 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between pb-4">
				{/* Search */}
				<div className="h-10 w-full lg:w-64 rounded-lg bg-neutral-800/50" />
				{/* Right filters */}
				<div className="flex items-center gap-2">
					{/* Todos/Activos/Inactivos buttons */}
					<div className="flex items-center gap-1 rounded-lg border border-neutral-800 bg-neutral-900/50 p-1">
						<div className="h-7 w-14 rounded-md bg-neutral-800/60" />
						<div className="h-7 w-16 rounded-md bg-neutral-800/40" />
						<div className="h-7 w-[4.5rem] rounded-md bg-neutral-800/40" />
					</div>
					{/* Select dropdowns */}
					<div className="h-9 w-32 rounded-lg bg-neutral-800/40" />
					<div className="h-9 w-28 rounded-lg bg-neutral-800/40" />
				</div>
			</div>

			{/* Table skeleton */}
			<div className="min-h-0 flex-1 overflow-hidden">
				{/* Table header */}
				<div className="border-b border-neutral-800 bg-neutral-900/50 px-4 py-3 flex items-center gap-4">
					<div className="w-8" /> {/* Search icon column */}
					<div className="h-3 w-14 rounded bg-neutral-700/60" /> {/* Brand */}
					<div className="h-3 w-16 rounded bg-neutral-700/60 flex-1" /> {/* Model */}
					<div className="h-3 w-12 rounded bg-neutral-700/60" /> {/* Storage */}
					<div className="h-3 w-10 rounded bg-neutral-700/60" /> {/* RAM */}
					<div className="h-3 w-14 rounded bg-neutral-700/60" /> {/* Price */}
					<div className="h-3 w-12 rounded bg-neutral-700/60" /> {/* Stock */}
					<div className="h-3 w-14 rounded bg-neutral-700/60" /> {/* Status */}
					<div className="w-16" /> {/* Actions */}
				</div>

				{/* Table rows */}
				{[...Array(6)].map((_, i) => (
					<div
						key={i}
						className="border-b border-neutral-800/50 px-4 py-3.5 flex items-center gap-4"
						style={{ opacity: 1 - i * 0.12 }}
					>
						{/* Search icon */}
						<div className="size-6 rounded bg-neutral-800/40" />
						{/* Brand */}
						<div className="h-4 rounded bg-neutral-800/70" style={{ width: `${50 + (i % 3) * 10}px` }} />
						{/* Model */}
						<div className="h-4 rounded bg-neutral-800/50 flex-1" style={{ maxWidth: `${100 + (i % 4) * 20}px` }} />
						{/* Storage */}
						<div className="h-4 w-14 rounded bg-neutral-800/40" />
						{/* RAM */}
						<div className="h-4 w-12 rounded bg-neutral-800/40" />
						{/* Price */}
						<div className="h-4 w-16 rounded bg-neutral-800/35" />
						{/* Stock */}
						<div className="h-4 w-8 rounded bg-neutral-800/35" />
						{/* Status toggle */}
						<div className="h-6 w-11 rounded-full bg-neutral-800/30" />
						{/* Actions */}
						<div className="flex gap-1">
							<div className="size-7 rounded bg-neutral-800/25" />
							<div className="size-7 rounded bg-neutral-800/25" />
						</div>
					</div>
				))}
			</div>

			{/* Footer skeleton */}
			<div className="shrink-0 flex items-center justify-between pt-3">
				<div className="h-3 w-28 rounded bg-neutral-800/40" />
				<div className="flex items-center gap-2">
					<div className="size-7 rounded bg-neutral-800/30" />
					<div className="h-4 w-14 rounded bg-neutral-800/35" />
					<div className="size-7 rounded bg-neutral-800/30" />
				</div>
			</div>
		</div>
	);
}

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 50];

function Pagination({ table }: { table: any }) {
	const pageCount = table.getPageCount();
	const currentPage = table.getState().pagination.pageIndex;
	const pageSize = table.getState().pagination.pageSize;
	const totalRows = table.getFilteredRowModel().rows.length;

	// Generate page options for Select
	const pageOptions = useMemo(() => {
		return Array.from({ length: pageCount }, (_, i) => ({
			label: String(i + 1),
			value: String(i)
		}));
	}, [pageCount]);

	// Generate page size options
	const pageSizeOptions = useMemo(() => {
		return PAGE_SIZE_OPTIONS.map((size) => ({
			label: `${size}`,
			value: String(size)
		}));
	}, []);

	if (pageCount <= 1) return null;

	const startRow = currentPage * pageSize + 1;
	const endRow = Math.min((currentPage + 1) * pageSize, totalRows);

	return (
		<div className="flex items-center gap-4">
			<div className="flex items-center gap-2">
				<span className="text-sm text-neutral-500">
					{startRow}-{endRow} de {totalRows}
				</span>
				<Select
					options={pageSizeOptions}
					defaultValue={String(pageSize)}
					onSelectedChange={(value) => table.setPageSize(Number(value))}
					className="!h-7 !text-xs !bg-neutral-900 !min-w-[90px]"
				/>
			</div>
			<div className="flex items-center gap-1">
				<button
					onClick={() => table.previousPage()}
					disabled={!table.getCanPreviousPage()}
					className="p-1.5 rounded-md hover:bg-neutral-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
				>
					<ChevronLeft className="size-4" />
				</button>
				<Select
					options={pageOptions}
					defaultValue={String(currentPage)}
					onSelectedChange={(value) => table.setPageIndex(Number(value))}
					className="!h-7 !text-xs !bg-neutral-900 !min-w-[50px] !w-[50px]"
				/>
				<span className="text-sm text-neutral-500">/ {pageCount}</span>
				<button
					onClick={() => table.nextPage()}
					disabled={!table.getCanNextPage()}
					className="p-1.5 rounded-md hover:bg-neutral-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
				>
					<ChevronRight className="size-4" />
				</button>
			</div>
		</div>
	);
}

function ProductInfoDialog({ product, onClose }: { product: StoreProduct; onClose: () => void }) {
	const info = product.product_info;

	return (
		<Dialog title="Información del producto" onClose={onClose} closable cerrarButton={false}>
			<div className="flex flex-col gap-6 min-w-[400px]">
				{/* Image placeholder */}
				<div className="flex justify-center">
					<div className="w-48 h-48 rounded-xl bg-neutral-800/50 border border-neutral-700 flex flex-col items-center justify-center text-neutral-500">
						<Smartphone className="size-16 mb-2 opacity-50" />
						<span className="text-xs">Imagen no disponible</span>
					</div>
				</div>

				{/* Device name */}
				<div className="text-center">
					<h3 className="text-lg font-semibold text-neutral-100">{info?.device_commercial_name || '-'}</h3>
					<p className="text-sm text-neutral-400">{info?.brand || '-'}</p>
				</div>

				{/* Colors */}
				{info?.available_colors && info.available_colors.length > 0 && (
					<div className="px-4 py-3 bg-neutral-800/30 rounded-lg">
						<span className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">Colores disponibles</span>
						<div className="flex flex-wrap gap-2 mt-1.5">
							{info.available_colors.map((color, i) => {
								const name = typeof color === 'string' ? color : color.name;
								const hex = typeof color === 'string' ? '#94a3b8' : color.hex;
								return (
									<div key={name || i} className="flex items-center gap-1.5">
										<span
											className="inline-block size-4 rounded-full border border-neutral-600"
											style={{ backgroundColor: hex }}
										/>
										<span className="text-sm text-neutral-100 capitalize">{name}</span>
									</div>
								);
							})}
						</div>
					</div>
				)}

				{/* Specs grid */}
				<div className="grid grid-cols-2 gap-3">
					<div className="px-4 py-3 bg-neutral-800/30 rounded-lg">
						<span className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">Almacenamiento</span>
						<p className="text-sm font-medium text-neutral-100 mt-0.5">{formatStorageOrRam(info?.storage)}</p>
					</div>
					<div className="px-4 py-3 bg-neutral-800/30 rounded-lg">
						<span className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">RAM</span>
						<p className="text-sm font-medium text-neutral-100 mt-0.5">{formatStorageOrRam(info?.ram)}</p>
					</div>
					<div className="px-4 py-3 bg-neutral-800/30 rounded-lg">
						<span className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">Pantalla</span>
						<p className="text-sm font-medium text-neutral-100 mt-0.5">{info?.display || '-'}</p>
					</div>
					<div className="px-4 py-3 bg-neutral-800/30 rounded-lg">
						<span className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">Batería</span>
						<p className="text-sm font-medium text-neutral-100 mt-0.5">{info?.battery || '-'}</p>
					</div>
					<div className="px-4 py-3 bg-neutral-800/30 rounded-lg col-span-2">
						<span className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">Procesador</span>
						<p className="text-sm font-medium text-neutral-100 mt-0.5">{info?.processor || '-'}</p>
					</div>
				</div>

				{/* Inventory info */}
				<div className="border-t border-neutral-800 pt-4">
					<span className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">En tu inventario</span>
					<div className="flex items-center gap-4 mt-2">
						<div>
							<span className="text-xs text-neutral-500">Precio:</span>
							<span className="ml-1.5 text-sm font-medium text-neutral-100">
								${product.price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
							</span>
						</div>
						<div>
							<span className="text-xs text-neutral-500">Stock:</span>
							<span className="ml-1.5 text-sm font-medium text-neutral-100">{product.quantity ?? '∞'}</span>
						</div>
						<div>
							<span className="text-xs text-neutral-500">Estado:</span>
							<span className={classNames('ml-1.5 text-sm font-medium', product.available ? 'text-green-400' : 'text-neutral-500')}>
								{product.available ? 'Activo' : 'Inactivo'}
							</span>
						</div>
					</div>
				</div>

				<Button type="button" variant="secondary" onClick={onClose} className="mt-2">
					Cerrar
				</Button>
			</div>
		</Dialog>
	);
}

function ProductFormDialog({
	devices,
	existingProductIds,
	editingProduct,
	onSave,
	onClose,
	loading,
	devicesLoading,
	error
}: {
	devices: DeviceModel[];
	existingProductIds: Set<string>;
	editingProduct: StoreProduct | null;
	onSave: (form: { device_id: string; price: string; quantity: string; available: boolean }) => Promise<void>;
	onClose: () => void;
	loading: boolean;
	devicesLoading: boolean;
	error: string | null;
}) {
	const [form, setForm] = useState({ device_id: '', price: '', quantity: '', available: true });

	useEffect(() => {
		if (editingProduct) {
			const device = devices.find((d) => d.device_commercial_name === editingProduct.product_info?.device_commercial_name);
			setForm({
				device_id: device?.id || '',
				price: String(editingProduct.price),
				quantity: editingProduct.quantity != null ? String(editingProduct.quantity) : '',
				available: editingProduct.available
			});
		} else {
			setForm({ device_id: '', price: '', quantity: '', available: true });
		}
	}, [editingProduct, devices]);

	// Convert devices to SelectableItem format for ProductTable
	const selectableItems: SelectableItem[] = useMemo(() => {
		return devices.map((d) => ({
			id: d.id,
			brand: d.brand,
			name: d.device_commercial_name,
			storage: d.storage,
			ram: d.ram
		}));
	}, [devices]);

	const selectedIds = useMemo(() => new Set(form.device_id ? [form.device_id] : []), [form.device_id]);
	const selectedDevice = devices.find((d) => d.id === form.device_id);

	const handleSelectionChange = (ids: Set<string>) => {
		const selectedId = Array.from(ids)[0] || '';
		setForm((f) => ({ ...f, device_id: selectedId }));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!editingProduct && !form.device_id) return;
		if (!form.price) return;
		await onSave(form);
	};

	return (
		<Dialog
			title={editingProduct ? 'Editar producto' : 'Nuevo producto'}
			onClose={onClose}
			closable
			cerrarButton={false}
			disableOverlayClose={!editingProduct}
			noContentWrapper={!editingProduct && !selectedDevice}
		>
			<form onSubmit={handleSubmit} className={classNames('flex flex-col', !editingProduct && !selectedDevice ? 'w-[600px] min-h-0 flex-1' : 'min-w-[320px] gap-5')}>
				{editingProduct ? (
					<div className="space-y-3">
						<div className="space-y-1">
							<span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">Dispositivo</span>
							<div className="px-3 py-2.5 bg-neutral-800/50 rounded-lg text-sm text-neutral-300">
								{editingProduct.product_info?.brand} {editingProduct.product_info?.device_commercial_name}
							</div>
						</div>
						<div className="flex gap-3">
							<div className="flex-1 px-3 py-2 bg-neutral-800/30 rounded-lg">
								<span className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">Almacenamiento</span>
								<p className="text-sm text-neutral-300">{formatStorageOrRam(editingProduct.product_info?.storage)}</p>
							</div>
							<div className="flex-1 px-3 py-2 bg-neutral-800/30 rounded-lg">
								<span className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">RAM</span>
								<p className="text-sm text-neutral-300">{formatStorageOrRam(editingProduct.product_info?.ram)}</p>
							</div>
						</div>
					</div>
				) : selectedDevice ? (
					<div className="space-y-2">
						<span className="text-xs font-medium text-neutral-400 uppercase tracking-wider">Dispositivo</span>
						<div className="flex items-center justify-between px-3 py-2.5 bg-primary-500/10 border border-primary-500/30 rounded-lg">
							<span className="text-sm">
								<span className="text-primary-200">{selectedDevice.brand}</span>
								<span className="text-primary-400"> {selectedDevice.device_commercial_name}</span>
							</span>
							<button
								type="button"
								onClick={() => setForm((f) => ({ ...f, device_id: '' }))}
								className="text-primary-400 hover:text-primary-300 text-xs"
							>
								Cambiar
							</button>
						</div>
						<div className="flex gap-3">
							<div className="flex-1 px-3 py-2 bg-neutral-800/30 rounded-lg">
								<span className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">Almacenamiento</span>
								<p className="text-sm text-neutral-300">{formatStorageOrRam(selectedDevice.storage)}</p>
							</div>
							<div className="flex-1 px-3 py-2 bg-neutral-800/30 rounded-lg">
								<span className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">RAM</span>
								<p className="text-sm text-neutral-300">{formatStorageOrRam(selectedDevice.ram)}</p>
							</div>
						</div>
					</div>
				) : (
					<div className="flex-1 min-h-0 flex flex-col">
						<ProductTable
							items={selectableItems}
							selectedIds={selectedIds}
							onSelectionChange={handleSelectionChange}
							singleSelect
							disabledIds={existingProductIds}
							emptyMessage="No hay dispositivos disponibles"
							emptyFilterMessage="No se encontraron dispositivos con esos filtros"
							pageSize={10}
							loading={devicesLoading}
						/>
					</div>
				)}

				{(editingProduct || selectedDevice) && (
					<>
						<div className="grid grid-cols-2 gap-4">
							<Input
								floatingLabel="Precio"
								placeholder="0.00"
								prefix="$"
								value={form.price}
								onChange={(value) => setForm((f) => ({ ...f, price: value }))}
								allowedChars="numeric"
								clearable={false}
							/>
							<Input
								floatingLabel="Stock"
								placeholder="Ilimitado"
								value={form.quantity}
								onChange={(value) => setForm((f) => ({ ...f, quantity: value }))}
								allowedChars="numbers_only"
								clearable={false}
							/>
						</div>

						<div className="flex items-center justify-between py-2">
							<span className="text-sm text-neutral-300">Disponible para venta</span>
							<Switch checked={form.available} onCheckedChange={(checked) => setForm((f) => ({ ...f, available: checked }))} />
						</div>
					</>
				)}

				{error && <Notice message={error} variant="error" className="shrink-0" />}

				<div className="shrink-0 flex gap-3 pt-4">
					<Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
						Cancelar
					</Button>
					<Button
						type="submit"
						className="flex-1"
						loading={loading}
						disabled={(!editingProduct && !form.device_id) || !form.price}
					>
						{editingProduct ? 'Guardar' : 'Agregar'}
					</Button>
				</div>
			</form>
		</Dialog>
	);
}

function BatchAddDialog({
	devices,
	existingProductIds,
	onAdd,
	onClose,
	loading,
	devicesLoading,
	error
}: {
	devices: DeviceModel[];
	existingProductIds: Set<string>;
	onAdd: (deviceIds: string[]) => Promise<void>;
	onClose: () => void;
	loading: boolean;
	devicesLoading: boolean;
	error: string | null;
}) {
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

	// Convert all devices to SelectableItem format
	const selectableItems: SelectableItem[] = useMemo(() => {
		return devices.map((d) => ({
			id: d.id,
			brand: d.brand,
			name: d.device_commercial_name,
			storage: d.storage,
			ram: d.ram
		}));
	}, [devices]);

	const handleAdd = async () => {
		if (selectedIds.size === 0) return;
		await onAdd(Array.from(selectedIds));
	};

	return (
		<Dialog title="Agregar varios productos" onClose={onClose} closable cerrarButton={false} disableOverlayClose noContentWrapper>
			<div className="flex flex-col w-[700px] min-h-0 flex-1">
				<ProductTable
					items={selectableItems}
					selectedIds={selectedIds}
					onSelectionChange={setSelectedIds}
					disabledIds={existingProductIds}
					emptyMessage="No hay dispositivos disponibles"
					emptyFilterMessage="No se encontraron dispositivos con esos filtros"
					pageSize={15}
					loading={devicesLoading}
				/>

				{error && <Notice message={error} variant="error" className="shrink-0 mt-4" />}

				<div className="shrink-0 flex gap-3 pt-4">
					<Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
						Cancelar
					</Button>
					<Button onClick={handleAdd} className="flex-1" loading={loading} disabled={selectedIds.size === 0}>
						Agregar {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
					</Button>
				</div>
			</div>
		</Dialog>
	);
}

export default function Inventario() {
	const { userData } = useContext(GlobalContext);

	const [products, setProducts] = useState<StoreProduct[]>([]);
	const [devices, setDevices] = useState<DeviceModel[]>([]);
	const [devicesLoading, setDevicesLoading] = useState(true);
	const [loading, setLoading] = useState(true);
	const [fetchError, setFetchError] = useState<string | null>(null);
	const [filter, setFilter] = useState('');
	const [sorting, setSorting] = useState<SortingState>([]);

	// Filters
	const [brandFilter, setBrandFilter] = useState<string>('all');
	const [statusFilter, setStatusFilter] = useState<string>('all');
	const [stockFilter, setStockFilter] = useState<string>('all');

	const [formOpen, setFormOpen] = useState(false);
	const [editingProduct, setEditingProduct] = useState<StoreProduct | null>(null);
	const [productToDelete, setProductToDelete] = useState<StoreProduct | null>(null);
	const [productInCatalogs, setProductInCatalogs] = useState<StoreProduct | null>(null);
	const [confirmForceDelete, setConfirmForceDelete] = useState(false);
	const [forceDeleteLoading, setForceDeleteLoading] = useState(false);
	const [forceDeleteError, setForceDeleteError] = useState<string | null>(null);
	const [formLoading, setFormLoading] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);

	// Batch add state
	const [batchAddOpen, setBatchAddOpen] = useState(false);
	const [batchAddLoading, setBatchAddLoading] = useState(false);
	const [batchAddError, setBatchAddError] = useState<string | null>(null);

	// Inline editing
	const [editingCell, setEditingCell] = useState<{ id: string; field: 'price' | 'quantity' } | null>(null);
	const [savingInline, setSavingInline] = useState<string | null>(null);
	const [inlineError, setInlineError] = useState<string | null>(null);

	// Product info dialog
	const [productInfoOpen, setProductInfoOpen] = useState<StoreProduct | null>(null);

	const fetchProducts = useCallback(async () => {
		setLoading(true);
		setFetchError(null);
		try {
			const [res, error] = await callAPI(
				'https://api.krece.app/storeprofile/store_products/',
				'GET',
				{ limit: 500 },
				userData
			);
			if (error) throw new Error(error);
			setProducts(Array.isArray(res) ? res : []);
		} catch (err) {
			console.error('Error fetching products:', err);
			setFetchError('Error al cargar los productos');
		} finally {
			setLoading(false);
		}
	}, [userData]);

	const fetchDevices = useCallback(async () => {
		setDevicesLoading(true);
		try {
			const [res, error] = await callAPI(
				'https://api.krece.app/storeprofile/devices_models/',
				'GET',
				{ limit: 5000 },
				userData
			);
			if (error) throw new Error(error);
			setDevices(Array.isArray(res) ? res : []);
		} catch (err) {
			console.error('Error fetching devices:', err);
			// Devices error is non-fatal - dialogs will show empty state
		} finally {
			setDevicesLoading(false);
		}
	}, [userData]);

	useEffect(() => {
		fetchProducts();
		fetchDevices();
	}, [fetchProducts, fetchDevices]);

	const handleOpenForm = (product?: StoreProduct) => {
		setEditingProduct(product || null);
		setFormOpen(true);
	};

	const handleCloseForm = () => {
		setFormLoading(false);
		setFormOpen(false);
		setEditingProduct(null);
		setFormError(null);
	};

	const handleSaveProduct = async (form: { device_id: string; price: string; quantity: string; available: boolean }) => {
		// For new products, device_id is required
		if (!editingProduct && !form.device_id) return;

		const selectedDevice = devices.find((d) => d.id === form.device_id);
		// Use existing product name when editing, otherwise use the selected device name
		const name = editingProduct?.product_info?.device_commercial_name || selectedDevice?.device_commercial_name || '';

		setFormLoading(true);
		setFormError(null);

		const isEditing = !!editingProduct;
		const productId = editingProduct?.id || '';

		try {
			let apiError: string | null = null;

			if (isEditing) {
				const [, error] = await callAPI(
					'https://api.krece.app/storeprofile/store_products/',
					'PUT',
					{
						product_id: productId,
						name,
						price: Number(form.price),
						quantity: form.quantity ? Number(form.quantity) : null,
						available: form.available
					},
					userData
				);
				apiError = error;
			} else {
				const [, error] = await callAPI(
					'https://api.krece.app/storeprofile/store_products/',
					'POST',
					{
						related_device_model_id: form.device_id,
						name,
						price: Number(form.price),
						quantity: form.quantity ? Number(form.quantity) : null,
						available: form.available
					},
					userData
				);
				apiError = error;
			}

			if (apiError) {
				setFormError(apiError);
				setFormLoading(false);
				return;
			}

			// Success - close modal first
			handleCloseForm();

			// Then refresh products list
			fetchProducts();
		} catch (err) {
			console.error('Error saving product:', err);
			setFormError('Ha ocurrido un error inesperado');
			setFormLoading(false);
		}
	};

	const handleDeleteProduct = async (): Promise<true | string> => {
		if (!productToDelete) return 'No se encontró el producto';
		try {
			const [res, error] = await callAPI(
				'https://api.krece.app/storeprofile/store_products/',
				'DELETE',
				{ product_id: productToDelete.id },
				userData
			);
			if (error) return error;
			if (res && res.products_deleted === false) {
				// Product is in catalogs - show option to remove from catalogs first
				setProductInCatalogs(productToDelete);
				setProductToDelete(null);
				return true; // Close the current dialog
			}
			fetchProducts();
			return true;
		} catch (err) {
			console.error('Error deleting product:', err);
			return 'Error al eliminar el producto';
		}
	};

	const handleForceDeleteProduct = async (): Promise<true | string> => {
		if (!productInCatalogs) return 'No se encontró el producto';

		setForceDeleteLoading(true);
		setForceDeleteError(null);

		try {
			const [res, error] = await callAPI(
				'https://api.krece.app/storeprofile/store_product_delete_from_catalogs/',
				'POST',
				{ product_id: productInCatalogs.id },
				userData
			);

			if (error) {
				setForceDeleteError(error);
				setForceDeleteLoading(false);
				return error;
			}

			if (!res?.success) {
				const errorMsg = 'Error al eliminar el producto';
				setForceDeleteError(errorMsg);
				setForceDeleteLoading(false);
				return errorMsg;
			}

			// Success - clean up and refresh
			setProductInCatalogs(null);
			setConfirmForceDelete(false);
			setForceDeleteLoading(false);
			fetchProducts();
			return true;
		} catch (err) {
			console.error('Error force deleting product:', err);
			const errorMsg = 'Error inesperado al eliminar el producto';
			setForceDeleteError(errorMsg);
			setForceDeleteLoading(false);
			return errorMsg;
		}
	};

	// Get existing product device IDs to filter out from batch add
	const existingProductDeviceIds = useMemo(() => {
		const ids = new Set<string>();
		products.forEach((p) => {
			// Find the device that matches this product's canonical name
			const device = devices.find((d) => d.device_commercial_name === p.product_info?.device_commercial_name);
			if (device) {
				ids.add(device.id);
			}
		});
		return ids;
	}, [products, devices]);

	const handleBatchAdd = async (deviceIds: string[]) => {
		if (deviceIds.length === 0) return;

		setBatchAddLoading(true);
		setBatchAddError(null);

		try {
			const productsToAdd = deviceIds
				.map((id) => {
					const device = devices.find((d) => d.id === id);
					if (!device) return null;
					return {
						related_device_model_id: device.id,
						name: device.device_commercial_name,
						available: false,
						price: 0,
						quantity: null
					};
				})
				.filter((p): p is NonNullable<typeof p> => p !== null);

			const [, error] = await callAPI(
				'https://api.krece.app/storeprofile/store_products/',
				'POST',
				{ products: productsToAdd },
				userData
			);

			if (error) {
				setBatchAddError(error);
				setBatchAddLoading(false);
				return;
			}

			setBatchAddOpen(false);
			setBatchAddError(null);
			fetchProducts();
		} catch (err) {
			console.error('Error batch adding products:', err);
			setBatchAddError('Ha ocurrido un error inesperado');
		} finally {
			setBatchAddLoading(false);
		}
	};

	const handleInlineUpdate = async (
		product: StoreProduct,
		field: 'price' | 'quantity' | 'available',
		value: number | boolean | null
	) => {
		// Close editing UI and optimistically update immediately
		setEditingCell(null);
		const oldValue = product[field];
		setProducts((prev) =>
			prev.map((p) => (p.id === product.id ? { ...p, [field]: value } : p))
		);

		setSavingInline(product.id);
		setInlineError(null);
		try {
			const updates = {
				price: field === 'price' ? (value as number) : product.price,
				quantity: field === 'quantity' ? (value as number | null) : product.quantity,
				available: field === 'available' ? (value as boolean) : product.available
			};

			const [, error] = await callAPI(
				'https://api.krece.app/storeprofile/store_products/',
				'PUT',
				{
					product_id: product.id,
					name: product.product_info?.device_commercial_name || '',
					...updates
				},
				userData
			);
			if (error) {
				// Revert on error
				setProducts((prev) =>
					prev.map((p) => (p.id === product.id ? { ...p, [field]: oldValue } : p))
				);
				setInlineError('Error al actualizar');
				setTimeout(() => setInlineError(null), 3000);
			}
		} catch (err) {
			console.error('Error updating product:', err);
			// Revert on error
			setProducts((prev) =>
				prev.map((p) => (p.id === product.id ? { ...p, [field]: oldValue } : p))
			);
			setInlineError('Error al actualizar');
			setTimeout(() => setInlineError(null), 3000);
		} finally {
			setSavingInline(null);
		}
	};

	const columns = useMemo(
		() => [
			productColumnHelper.display({
				id: 'info',
				header: '',
				cell: ({ row }) => (
					<button
						onClick={(e) => {
							e.stopPropagation();
							setProductInfoOpen(row.original);
						}}
						className="p-1 rounded-md hover:bg-neutral-800 text-neutral-500 hover:text-neutral-300 transition-colors"
					>
						<Search className="size-4" />
					</button>
				)
			}),
			productColumnHelper.accessor((r) => r.product_info?.brand, {
				id: 'brand',
				header: ({ column }) => <SortHeader column={column}>Marca</SortHeader>,
				cell: (info) => <span className="font-medium text-neutral-100">{info.getValue() || '-'}</span>
			}),
			productColumnHelper.accessor((r) => r.product_info?.device_commercial_name, {
				id: 'name',
				header: ({ column }) => <SortHeader column={column}>Modelo</SortHeader>,
				cell: (info) => <span className="text-neutral-400">{info.getValue() || '-'}</span>
			}),
			productColumnHelper.accessor((r) => r.product_info?.storage, {
				id: 'storage',
				header: () => <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">Almac.</span>,
				cell: (info) => <span className="text-neutral-400 tabular-nums">{formatStorageOrRam(info.getValue())}</span>
			}),
			productColumnHelper.accessor((r) => r.product_info?.ram, {
				id: 'ram',
				header: () => <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">RAM</span>,
				cell: (info) => <span className="text-neutral-400 tabular-nums">{formatStorageOrRam(info.getValue())}</span>
			}),
			productColumnHelper.accessor('price', {
				header: ({ column }) => <SortHeader column={column}>Precio</SortHeader>,
				cell: ({ row, getValue }) => {
					const product = row.original;
					const value = getValue();
					const isEditing = editingCell?.id === product.id && editingCell?.field === 'price';
					const isSaving = savingInline === product.id;

					if (isEditing) {
						const savePrice = (inputValue: string) => {
							const newValue = parseFloat(inputValue);
							if (!isNaN(newValue) && newValue !== value) {
								handleInlineUpdate(product, 'price', newValue);
							} else {
								setEditingCell(null);
							}
						};
						return (
							<div className="flex items-center gap-1">
								<input
									id={`price-input-${product.id}`}
									type="text"
									autoFocus
									defaultValue={value?.toString() || ''}
									onBlur={() => setEditingCell(null)}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											savePrice(e.currentTarget.value);
										} else if (e.key === 'Escape') {
											setEditingCell(null);
										}
									}}
									className="w-20 h-7 px-2 rounded-l bg-neutral-800 border border-primary-500 text-sm text-neutral-100 font-medium focus:outline-none"
								/>
								<button
									type="button"
									onMouseDown={(e) => e.preventDefault()}
									onClick={() => {
										const input = document.getElementById(`price-input-${product.id}`) as HTMLInputElement;
										if (input) savePrice(input.value);
									}}
									className="h-7 px-2 rounded-r bg-primary-500 hover:bg-primary-400 text-white transition-colors"
								>
									<Check className="size-4" />
								</button>
							</div>
						);
					}

					return (
						<button
							onClick={() => setEditingCell({ id: product.id, field: 'price' })}
							disabled={isSaving}
							className="group flex items-center gap-1.5 font-medium text-neutral-100 hover:text-white transition-colors disabled:opacity-50"
						>
							<span>${value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
							<Pencil className="size-3 opacity-20 group-hover:opacity-60 transition-opacity" />
						</button>
					);
				}
			}),
			productColumnHelper.accessor('quantity', {
				header: ({ column }) => <SortHeader column={column}>Stock</SortHeader>,
				cell: ({ row, getValue }) => {
					const product = row.original;
					const qty = getValue();
					const isEditing = editingCell?.id === product.id && editingCell?.field === 'quantity';
					const isSaving = savingInline === product.id;

					if (isEditing) {
						const saveQuantity = (inputValue: string) => {
							const val = inputValue.trim();
							const newValue = val === '' ? null : parseInt(val);
							if (newValue !== qty) {
								handleInlineUpdate(product, 'quantity', newValue);
							} else {
								setEditingCell(null);
							}
						};
						return (
							<div className="flex items-center gap-1">
								<input
									id={`qty-input-${product.id}`}
									type="text"
									autoFocus
									defaultValue={qty?.toString() || ''}
									placeholder="∞"
									onBlur={() => setEditingCell(null)}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											saveQuantity(e.currentTarget.value);
										} else if (e.key === 'Escape') {
											setEditingCell(null);
										}
									}}
									className="w-16 h-7 px-2 rounded-l bg-neutral-800 border border-primary-500 text-sm text-neutral-100 focus:outline-none"
								/>
								<button
									type="button"
									onMouseDown={(e) => e.preventDefault()}
									onClick={() => {
										const input = document.getElementById(`qty-input-${product.id}`) as HTMLInputElement;
										if (input) saveQuantity(input.value);
									}}
									className="h-7 px-2 rounded-r bg-primary-500 hover:bg-primary-400 text-white transition-colors"
								>
									<Check className="size-4" />
								</button>
							</div>
						);
					}

					return (
						<button
							onClick={() => setEditingCell({ id: product.id, field: 'quantity' })}
							disabled={isSaving}
							className={classNames(
								'group flex items-center gap-1.5 tabular-nums transition-colors disabled:opacity-50',
								{
									'text-red-400 hover:text-red-300': qty === 0,
									'text-amber-400 hover:text-amber-300': qty !== null && qty > 0 && qty <= 5,
									'text-neutral-300 hover:text-neutral-100': qty === null || qty > 5
								}
							)}
						>
							<span>{qty ?? '∞'}</span>
							<Pencil className="size-3 opacity-20 group-hover:opacity-60 transition-opacity" />
						</button>
					);
				}
			}),
			productColumnHelper.accessor('available', {
				header: () => <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">Estado</span>,
				cell: ({ row, getValue }) => {
					const product = row.original;
					const isAvailable = getValue();
					const isSaving = savingInline === product.id;

					return (
						<button
							onClick={() => handleInlineUpdate(product, 'available', !isAvailable)}
							disabled={isSaving}
							className={classNames(
								'relative h-6 w-11 rounded-full transition-colors disabled:opacity-50',
								isAvailable ? 'bg-green-500' : 'bg-neutral-700'
							)}
						>
							<span
								className={classNames(
									'absolute top-1 left-1 size-4 rounded-full bg-white transition-transform shadow-sm',
									isAvailable && 'translate-x-5'
								)}
							/>
						</button>
					);
				}
			}),
			productColumnHelper.display({
				id: 'actions',
				header: '',
				cell: ({ row }) => (
					<div className="flex items-center justify-end gap-1">
						<button
							onClick={() => handleOpenForm(row.original)}
							className="p-1.5 rounded-md hover:bg-neutral-800 text-neutral-500 hover:text-neutral-300 transition-colors"
						>
							<Pencil className="size-4" />
						</button>
						<button
							onClick={() => setProductToDelete(row.original)}
							className="p-1.5 rounded-md hover:bg-red-500/10 text-neutral-500 hover:text-red-400 transition-colors"
						>
							<Trash2 className="size-4" />
						</button>
					</div>
				)
			})
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[editingCell, savingInline]
	);

	// Get unique brands for filter
	const brandOptions = useMemo(() => {
		const brands = [...new Set(products.map((p) => p.product_info?.brand).filter(Boolean))].sort();
		return [{ label: 'Todas las marcas', value: 'all' }, ...brands.map((b) => ({ label: b, value: b }))];
	}, [products]);

	// Apply filters
	const filteredProducts = useMemo(() => {
		return products.filter((p) => {
			// Brand filter
			if (brandFilter !== 'all' && p.product_info?.brand !== brandFilter) return false;
			// Status filter
			if (statusFilter === 'active' && !p.available) return false;
			if (statusFilter === 'inactive' && p.available) return false;
			// Stock filter
			if (stockFilter === 'in_stock' && (p.quantity !== null && p.quantity === 0)) return false;
			if (stockFilter === 'low_stock' && (p.quantity === null || p.quantity > 5 || p.quantity === 0)) return false;
			if (stockFilter === 'out_of_stock' && (p.quantity === null || p.quantity > 0)) return false;
			return true;
		});
	}, [products, brandFilter, statusFilter, stockFilter]);

	const table = useReactTable({
		data: filteredProducts,
		columns,
		state: { sorting, globalFilter: filter },
		onSortingChange: setSorting,
		onGlobalFilterChange: setFilter,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: { pagination: { pageSize: 10 } }
	});

	if (!userData?.email) return null;

	return (
		<div className="flex h-full w-full flex-col p-4 md:p-6">
			{/* Header - Fixed */}
			<div className="flex-shrink-0 flex flex-col gap-4 pb-4">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<h1 className="text-2xl font-semibold tracking-tight">Inventario</h1>
						{products.length > 0 && (
							<button
								onClick={fetchProducts}
								disabled={loading}
								className="p-2 rounded-lg border border-neutral-800 bg-neutral-900/50 hover:bg-neutral-800 disabled:opacity-50 transition-colors"
								title="Recargar"
							>
								<RefreshCw className={classNames('size-4 text-neutral-400', loading && 'animate-spin')} />
							</button>
						)}
					</div>
					<div className="flex items-center gap-2 shrink-0">
						<Button onClick={() => setBatchAddOpen(true)} variant="secondary" className="gap-1.5 w-max">
							<Layers className="size-4 shrink-0" />
							Agregar varios
						</Button>
						<Button onClick={() => handleOpenForm()} className="gap-1.5 w-max">
							<Plus className="size-4 shrink-0" />
							Agregar uno
						</Button>
					</div>
				</div>

				{products.length > 0 && (
					<div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
						{/* Left: Search */}
						<div className="flex items-center gap-2">
							<div className="relative flex-1 lg:w-64 lg:flex-none">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-neutral-500 pointer-events-none" />
								<input
									type="text"
									placeholder="Buscar..."
									value={filter}
									onChange={(e) => setFilter(e.target.value)}
									className="w-full h-10 pl-9 pr-3 rounded-lg bg-neutral-900/50 border border-neutral-800 text-sm text-neutral-100 placeholder:text-neutral-500 focus:outline-none focus:border-neutral-700 transition-colors"
								/>
							</div>
						</div>

						{/* Right: Filters */}
						<div className="-mx-4 px-4 md:mx-0 md:px-0 flex items-center gap-2 overflow-x-auto no-scrollbar">
							<div className="flex items-center gap-1 rounded-lg border border-neutral-800 bg-neutral-900/50 p-1 shrink-0">
								{[
									{ label: 'Todos', value: 'all' },
									{ label: 'Activos', value: 'active' },
									{ label: 'Inactivos', value: 'inactive' }
								].map((opt) => (
									<button
										key={opt.value}
										onClick={() => setStatusFilter(opt.value)}
										className={classNames(
											'px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-all',
											statusFilter === opt.value
												? 'bg-primary-500 text-white'
												: 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800'
										)}
									>
										{opt.label}
									</button>
								))}
							</div>

							<div className="h-6 w-px bg-neutral-800 shrink-0 hidden lg:block" />

							<Select
								options={brandOptions}
								defaultValue={brandFilter}
								placeholder="Marca"
								onSelectedChange={(value) => setBrandFilter((value as string) || 'all')}
								className="!h-9 !text-xs !bg-neutral-900/50 shrink-0"
							/>

							<Select
								options={[
									{ label: 'Todo el stock', value: 'all' },
									{ label: 'En stock', value: 'in_stock' },
									{ label: 'Stock bajo', value: 'low_stock' },
									{ label: 'Sin stock', value: 'out_of_stock' }
								]}
								defaultValue={stockFilter}
								placeholder="Stock"
								onSelectedChange={(value) => setStockFilter((value as string) || 'all')}
								className="!h-9 !text-xs !bg-neutral-900/50 shrink-0"
							/>
						</div>
					</div>
				)}
			</div>

			{/* Table Container - Fills remaining space */}
			<div className="relative flex min-h-0 flex-1 flex-col">
				{loading && products.length > 0 && (
					<div className="absolute inset-0 bg-foreground/50 z-10 flex items-center justify-center rounded-lg">
						<Spinner size={1.5} />
					</div>
				)}

				{loading && products.length === 0 ? (
					<TableSkeleton />
				) : fetchError && products.length === 0 ? (
					<div className="flex flex-1 flex-col items-center justify-center text-center">
						<PackageOpen className="size-12 text-red-500/50 mb-4" strokeWidth={1.5} />
						<p className="text-neutral-300 font-medium mb-1">{fetchError}</p>
						<p className="text-sm text-neutral-500 mb-4">No se pudieron cargar los productos</p>
						<Button onClick={fetchProducts} size="sm" variant="secondary" className="gap-1.5">
							<RefreshCw className="size-4" />
							Reintentar
						</Button>
					</div>
				) : !loading && products.length === 0 ? (
					<div className="flex flex-1 flex-col items-center justify-center text-center">
						<PackageOpen className="size-12 text-neutral-700 mb-4" strokeWidth={1.5} />
						<p className="text-neutral-300 font-medium mb-1">Sin productos</p>
						<p className="text-sm text-neutral-500 mb-4">Agrega tu primer producto al inventario</p>
						<Button onClick={() => setBatchAddOpen(true)} size="sm" className="gap-1.5">
							<Plus className="size-4" />
							Agregar productos
						</Button>
					</div>
				) : (
					<>
						{/* Scrollable table area */}
						<div className="min-h-0 flex-1 overflow-auto">
							<table className="w-full">
								<thead className="sticky top-0 z-[1] bg-foreground">
									{table.getHeaderGroups().map((hg) => (
										<tr key={hg.id} className="border-b border-neutral-800">
											{hg.headers.map((h) => (
												<th key={h.id} className="px-4 py-3 text-left bg-foreground">
													{h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
												</th>
											))}
										</tr>
									))}
								</thead>
								<tbody>
									{table.getRowModel().rows.map((row) => (
										<tr
											key={row.id}
											className="border-b border-neutral-800/50 hover:bg-neutral-800/20 transition-colors"
										>
											{row.getVisibleCells().map((cell) => (
												<td key={cell.id} className="px-4 py-3 text-sm">
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</td>
											))}
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{/* Footer - Fixed at bottom */}
						<div className="flex-shrink-0 flex items-center justify-end border-t border-neutral-800 bg-foreground pt-3 text-sm text-neutral-500">
							<Pagination table={table} />
						</div>

						{/* Inline update error toast */}
						{inlineError && (
							<div className="absolute bottom-16 left-1/2 -translate-x-1/2 px-4 py-2 bg-red-500/90 text-white text-sm font-medium rounded-lg shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-200">
								{inlineError}
							</div>
						)}
					</>
				)}
			</div>

			{/* Form Dialog */}
			{formOpen && (
				<ProductFormDialog
					devices={devices}
					existingProductIds={existingProductDeviceIds}
					editingProduct={editingProduct}
					onSave={handleSaveProduct}
					onClose={handleCloseForm}
					loading={formLoading}
					devicesLoading={devicesLoading}
					error={formError}
				/>
			)}

			{/* Delete Confirmation */}
			{productToDelete && (
				<ConfirmDialog
					title="Eliminar producto"
					message={`¿Eliminar ${productToDelete.product_info?.brand} ${productToDelete.product_info?.device_commercial_name} del inventario?`}
					onConfirm={handleDeleteProduct}
					onClose={() => setProductToDelete(null)}
				/>
			)}

			{/* Product in Catalogs - First Confirmation */}
			{productInCatalogs && !confirmForceDelete && (
				<Dialog
					title="Producto en catálogos"
					onClose={() => setProductInCatalogs(null)}
					closable
					cerrarButton={false}
				>
					<div className="flex flex-col gap-4 min-w-[320px] max-w-md">
						<p className="text-sm text-neutral-300">
							<span className="font-medium text-neutral-100">
								{productInCatalogs.product_info?.brand} {productInCatalogs.product_info?.device_commercial_name}
							</span>{' '}
							está incluido en uno o más catálogos.
						</p>
						<p className="text-sm text-neutral-400">
							¿Deseas eliminarlo de todos los catálogos y luego eliminarlo del inventario?
						</p>
						<div className="flex gap-3 pt-2">
							<Button
								type="button"
								variant="secondary"
								className="flex-1"
								onClick={() => setProductInCatalogs(null)}
							>
								Cancelar
							</Button>
							<Button
								type="button"
								variant="destructive"
								className="flex-1"
								onClick={() => setConfirmForceDelete(true)}
							>
								Sí, eliminar de todo
							</Button>
						</div>
					</div>
				</Dialog>
			)}

			{/* Product in Catalogs - Double Confirmation */}
			{productInCatalogs && confirmForceDelete && (
				<Dialog
					title="Confirmar eliminación"
					onClose={() => {
						setConfirmForceDelete(false);
						setForceDeleteError(null);
					}}
					closable={!forceDeleteLoading}
					cerrarButton={false}
				>
					<div className="flex flex-col gap-4 min-w-[320px] max-w-md">
						<p className="text-sm text-neutral-300">
							Esta acción eliminará{' '}
							<span className="font-medium text-neutral-100">
								{productInCatalogs.product_info?.brand} {productInCatalogs.product_info?.device_commercial_name}
							</span>{' '}
							de <span className="font-medium text-red-400">todos los catálogos</span> y del inventario.
						</p>
						<p className="text-sm text-amber-400 font-medium">
							Esta acción no se puede deshacer.
						</p>
						{forceDeleteError && (
							<Notice message={forceDeleteError} variant="error" />
						)}
						<div className="flex gap-3 pt-2">
							<Button
								type="button"
								variant="secondary"
								className="flex-1"
								onClick={() => {
									setConfirmForceDelete(false);
									setForceDeleteError(null);
								}}
								disabled={forceDeleteLoading}
							>
								Cancelar
							</Button>
							<Button
								type="button"
								variant="destructive"
								className="flex-1"
								onClick={handleForceDeleteProduct}
								loading={forceDeleteLoading}
							>
								Confirmar eliminación
							</Button>
						</div>
					</div>
				</Dialog>
			)}

			{/* Batch Add Dialog */}
			{batchAddOpen && (
				<BatchAddDialog
					devices={devices}
					existingProductIds={existingProductDeviceIds}
					onAdd={handleBatchAdd}
					onClose={() => {
						setBatchAddOpen(false);
						setBatchAddError(null);
					}}
					loading={batchAddLoading}
					devicesLoading={devicesLoading}
					error={batchAddError}
				/>
			)}

			{/* Product Info Dialog */}
			{productInfoOpen && <ProductInfoDialog product={productInfoOpen} onClose={() => setProductInfoOpen(null)} />}
		</div>
	);
}
